/**
 * Shared geometry sizing helpers. These live outside Nodes.tsx so that
 * Edges.tsx (and any future component) can compute node radii without
 * cyclic imports — important for the edge-clipping logic, which trims
 * each edge geometry so it ends at the visual boundary of the node
 * sphere instead of plunging through its center.
 */
import type { GraphNode, SizeBy } from '../types/domain';

/**
 * Visual radius of a node sphere in world units. Match the value used
 * for the InstancedMesh scale in Nodes.tsx exactly — if these drift,
 * edges will either overlap node geometry or leave a visible gap.
 */
export function nodeRadius(n: GraphNode, mode: SizeBy = 'degree'): number {
  if (n.kind === 'interception') return 0.32;

  // Size carries no information in this mode, so every node that is not an
  // interception reads the same. Useful for looking at structure without
  // the eye being dragged to the hubs.
  if (mode === 'uniform') return 0.9;

  // Orphans (degree 0): clickable floor. Visual "isolated" signal
  // comes from a dimmed color in Nodes.tsx, not from sphere size.
  if (n.degree === 0) return 0.65;
  // Degree-1: explicit floor a notch above orphans so a
  // single-connection node reads as a "real" node, distinct from
  // the disconnected ones, but still smaller than degree-2+ on the
  // curve below.
  if (n.degree === 1) return 0.75;

  /**
   * RANK — scale-free.
   *
   * The node's percentile in the degree distribution drives the radius, so
   * the busiest node lands at the same size whether it has 69 connections
   * or 205. The `degree` curve below cannot do that: it maps a raw count,
   * so it was tuned for a 62-node graph and drifted the moment the dataset
   * grew to 174. `standing` went from 69 connections to 205 and its radius
   * went with it, ending up 7x the median node by area.
   *
   * The exponent flattens the middle of the range. Percentile alone is
   * uniform by construction — half the nodes sit below 0.5 — which spends
   * most of the size range on distinctions between nodes that all have two
   * or three connections. Squaring pushes that crowd together and keeps
   * the visible spread for the genuinely busy end.
   */
  /**
   * RANK — position in the order, magnitude ignored.
   *
   * The exponent was 2, and squaring a percentile crushes exactly where
   * most nodes live: across the middle HALF of the graph the radius moved
   * 0.75 to 0.87, a 15% spread, which reads as every node being the same
   * size. 1.4 keeps the top from running away while leaving the middle
   * legible.
   */
  if (mode === 'rank') {
    const p = n.degreePercentile ?? 0;
    const base = 0.75 + Math.pow(p, 1.4) * 0.62;
    return n.isHub ? base * 1.1 : base;
  }

  /**
   * DEGREE — proportional to connection count, normalised against the
   * busiest node.
   *
   * The original was absolute: `0.6 + log1p(d) * 0.18` with a 1.45x hub
   * multiplier on top. Two things went wrong with it.
   *
   * It drifted. Tuned when the busiest node had 69 connections, it had no
   * idea `standing` would later have 205 — so the top of the range grew
   * with the dataset and ended up 7.1x the median node BY AREA, against
   * 4.9x on the map it was designed for.
   *
   * And the hub multiplier is a step function applied to the nodes that
   * are already largest, so it compounds precisely where the problem is.
   * A node one connection below q3 jumps 45% the moment it crosses.
   *
   * Dividing by log1p(maxDegree) fixes the drift: the busiest node sits
   * at the top of the range whatever that degree happens to be. The hub
   * boost drops to 1.08, enough to mark the tier without doubling down on
   * it — hubs already carry a bridge ring and their own tint.
   *
   * Result: 3.2x biggest-to-median by area rather than 7.1x, while the
   * middle half of the graph still grows 48% (rank manages 15%, which is
   * why rank reads as flat).
   */
  const norm = n.degreeNorm ?? Math.min(1, Math.log1p(n.degree) / Math.log1p(200));
  const base = 0.7 + norm * 0.8;
  return n.isHub ? base * 1.08 : base;
}

/**
 * The protagonist / centre node is NOT drawn by Nodes.tsx — CenterAnchor
 * renders it as its own mesh at this fixed radius, deliberately smaller
 * than its degree would imply so it reads as an anchor rather than a
 * giant blob. Anything that needs to size itself against the centre node
 * (e.g. the selection marker) must use THIS, not nodeRadius().
 */
export const CENTER_RADIUS = 1.05;

/** Visual radius of a node as actually drawn. */
export function visualRadius(n: GraphNode, isCenter: boolean, mode: SizeBy = 'degree'): number {
  return isCenter ? CENTER_RADIUS : nodeRadius(n, mode);
}

/**
 * Scale applied to a node's sphere when it's selected / hovered. Nodes.tsx
 * inflates the drawn geometry by these, so anything anchored to the
 * sphere's edge (labels) has to inflate with it or the node grows out
 * from under the anchor and swallows it.
 */
export const SELECTED_BOOST = 1.45;
export const HOVERED_BOOST = 1.28;

/** Outer edge of the bridge ring, relative to the sphere radius.
 *  Mirrors BridgeRing in Nodes.tsx: radiusFor(node) * 1.4, ringGeometry
 *  drawn out to r * 1.08. */
const BRIDGE_RING_FACTOR = 1.4 * 1.08;
/**
 * World gap between the outermost geometry and the label. Deliberately
 * small: the label has to read as BELONGING to its node, and at 0.55 it
 * floated far enough off to look like a caption for something else.
 * The Html anchor adds a further 4px of CSS breathing room on top, which
 * is constant in screen space and keeps the text off the edge at
 * close zoom.
 */
const LABEL_GAP = 0.18;

/**
 * World-space distance from a node's CENTRE to where its label should be
 * anchored.
 *
 * This used to be approximated independently in each label component —
 * ProximityLabels derived it from `weight` (which is not what sizes the
 * sphere) and HubLabels used a flat constant. Both under-shot the real
 * radius on big nodes, and neither knew about the 1.45x selection boost,
 * so selecting a hub grew the sphere straight up over its own name.
 *
 * Deriving it from the SAME nodeRadius()/visualRadius() the mesh uses
 * keeps the label clear of the sphere at every degree, zoom and state.
 */
export function labelAnchorOffset(
  n: GraphNode,
  opts: { isCenter?: boolean; boost?: number } = {},
): number {
  // `boost` is the ANIMATED hover/selection scale (see interactBoost.ts),
  // not the raw constant — passing the stepped value here is what made
  // labels teleport when a node was selected.
  const rest = visualRadius(n, opts.isCenter ?? false);
  const sphere = rest * (opts.boost ?? 1);
  // The bridge ring does NOT inflate with hover/selection — it holds its
  // resting size so the swelling sphere fills it. So the label clears
  // whichever is currently further out: the ring at rest, or the sphere
  // once it has grown past the ring. On a bridge node the ring usually
  // wins outright, which means its label doesn't move at all.
  const outer = n.isBridge
    ? Math.max(sphere, rest * BRIDGE_RING_FACTOR)
    : sphere;
  return outer + LABEL_GAP;
}
